import React, { createContext, useContext } from 'react';
import api from '../services/api';
import { toast } from 'react-toastify';

const UnitsContext = createContext();

export const useUnits = () => useContext(UnitsContext);

export const UnitsProvider = ({ children }) => {
  const toResultObject = (payload) => {
    if (Array.isArray(payload)) {
      return {
        count: payload.length,
        next: null,
        previous: null,
        results: payload,
      };
    }

    const results = Array.isArray(payload?.results) ? payload.results : [];
    return {
      ...(payload || {}),
      count: typeof payload?.count === 'number' ? payload.count : results.length,
      next: payload?.next || null,
      previous: payload?.previous || null,
      results,
    };
  };

  const buildQueryParams = (query = {}, fallbackKey = null) => {
    const params = {};
    if (query && typeof query === 'object' && !Array.isArray(query)) {
      Object.entries(query).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params[key] = value;
        }
      });
      return params;
    }

    if (fallbackKey && query !== undefined && query !== null && query !== '') {
      params[fallbackKey] = query;
    }

    return params;
  };

  const hasExplicitPagination = (params = {}) => (
    ['limit', 'offset', 'page', 'page_size'].some((key) => (
      params[key] !== undefined && params[key] !== null && params[key] !== ''
    ))
  );

  const fetchAllPages = async (url, headers, initialParams = {}) => {
    const aggregated = [];
    let nextUrl = url;
    let nextParams = initialParams;
    let lastPayload = null;

    while (nextUrl) {
      const response = await api.get(nextUrl, {
        headers,
        params: nextParams,
      });

      const normalized = toResultObject(response?.data);
      lastPayload = normalized;
      aggregated.push(...normalized.results);

      if (!normalized.next) {
        break;
      }

      nextUrl = normalized.next;
      nextParams = undefined;
    }

    return {
      ...(lastPayload || {}),
      count: aggregated.length,
      next: null,
      previous: null,
      results: aggregated,
    };
  };

  // Requisição cadastrar unidade
  const saveUnit = async (formData) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }

    try {
      const response = await api.post('units/unit/', formData, {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });

      toast.success('Unidade cadastrada com sucesso!');
      return response;
    } catch (error) {
      console.error('Erro ao cadastrar unidade', error);
      if (error.response && error.response.data) {
        // Exibir o detalhe do erro se disponível
        const errorMessage = error.response.data.detail || 'Erro ao cadastrar unidade.';
        toast.error(errorMessage);
      } else {
        // Erros que não são de resposta HTTP (como problemas de rede)
        toast.error(`Erro ao cadastrar unidade: ${error.message || 'Erro desconhecido'}`);
      }
    }
  };

  // Requisição listar unidades
  const listUnits = async () => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }

    try {
      const response = await api.get('units/unit/', {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });
      return response.data;
    } catch (error) {
      if (error.response) {
        console.error('Erro ao buscar unidades', error);
      } else {
        console.error('Erro ao buscar unidades', error);
      }
    }
  };

  // Lista nome e Id da unidades
  const summaryListUnit = async () => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }

    try {
      const response = await api.get('units/unit/summarylist/', {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });
      return response.data;
    } catch (error) {
      if (error.response) {
        console.error('Erro ao buscar unidades', error);
        toast.error(error.response.data.detail);
      } else {
        console.error('Erro ao buscar unidades', error);
        toast.error('Erro ao buscar unidades', error);
      }
    }
  };

  // Requisição para obter uma unidade específica pelo ID
  const getUnitById = async (unitId) => {
    try {
      const accessToken = localStorage.getItem('accessToken');
      if (!accessToken) {
        return;
      }

      const response = await api.get(`units/unit/${unitId}`, {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });

      return response.data;
    } catch (error) {
      if (error.response) {
        console.error('Erro ao buscar unidades pelo id', error);
        toast.error(error.response.data.detail);
      } else {
        console.error('Erro ao buscar unidades pelo id', error);
        toast.error('Erro ao buscar unidade pelo id', error);
      }
    }
  };

  // Requisição para editar uma unidade específica
  const updateUnit = async (unitId, unitData) => {
    try {
      const accessToken = localStorage.getItem('accessToken');
      if (!accessToken) {
        return;
      }

      const response = await api.patch(`units/unit/${unitId}/`, unitData, {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });

      toast.success('Unidade atualizada com sucesso');
      return response.data;
    } catch (error) {
      if (error.response) {
        console.error('Erro ao atualizar unidade', error);
        toast.error(error.response.data.detail);
      } else {
        console.error('Erro ao atualizar unidade', error);
        toast.error('Erro ao atualizar unidade', error);
      }
    }
  };

  // Requisição para atualizar um pavilhão
  const updatePavilion = async (pavilionId, pavilionData) => {
    try {
      const accessToken = localStorage.getItem('accessToken');
      if (!accessToken) return;
      const response = await api.patch(`units/pavilion/${pavilionId}/`, pavilionData, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });
      toast.success('Pavilhão atualizado com sucesso');
      return response.data;
    } catch (error) {
      if (error.response) {
        console.error('Erro ao atualizar pavilhão', error);
        toast.error(error.response.data.detail);
      } else {
        console.error('Erro ao atualizar pavilhão', error);
        toast.error('Erro ao atualizar pavilhão');
      }
    }
  };

  // Requisição para salvar um novo pavilhão
  const savePavilion = async (pavilionData) => {
    try {
      const accessToken = localStorage.getItem('accessToken');
      if (!accessToken) {
        return;
      }

      const response = await api.post('units/pavilion/', pavilionData, {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });

      toast.success('Pavilhão cadastrado com sucesso')
      return response.data;
    } catch (error) {
      if (error.response) {
        console.error('Erro ao cadastrar pavilhão', error);
        toast.error(error.response.data.detail);
      } else {
        console.error('Erro ao cadastrar pavilhão', error);
        toast.error('Erro ao cadastrar pavilhão', error);
      }
    }
  };

  // Lista Pavilhões
  const listPavillions = async (query = {}) => {
    try {
      const accessToken = localStorage.getItem('accessToken');
      if (!accessToken) {
        return;
      }

      const headers = {
        Authorization: `Bearer ${accessToken}`
      };

      const params = buildQueryParams(query, 'unit');
      if (!hasExplicitPagination(params)) {
        params.limit = 500;
        return await fetchAllPages('units/pavilion/', headers, params);
      }

      const response = await api.get('units/pavilion/', { headers, params });
      return toResultObject(response?.data);
    } catch (error) {
      if (error.response) {
        console.error('Erro ao buscar pavilhão', error);
        toast.error(error.response.data.detail);
      } else {
        console.error('Erro ao buscar pavilhão', error);
        toast.error('Erro ao buscar pavilhão', error);
      }
    }
  };

  // Lista serviços de terceiros
  const listThirdPartyServices = async () => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }

    try {
      const response = await api.get('units/third_party_service/', {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });
      return response.data;
    } catch (error) {
      if (error.response) {
        console.error('Erro ao buscar serviços de terceiros', error);
        toast.error(error.response.data.detail);
      } else {
        console.error('Erro ao buscar serviços de terceiros', error);
        toast.error('Erro ao buscar serviços de terceiros', error);
      }
    }
  };

  // Requisição Salvar equipamentos
  const saveEquipments = async (equipments) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }

    try {
      const response = await api.post('units/equipment/safety_equipments/', equipments, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      toast.success('Equipamentos cadastrados com sucesso!');
      return response;
    } catch (error) {
      console.error('Erro ao cadastrar equipamentos:', error);
      toast.error('Erro ao cadastrar equipamentos.');
    }
  };

  // Requisisão para listar os equipamentos
  const listEquipments = async (unitId) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }

    try {
      const response = await api.get(`units/equipment/unit/${unitId}/`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      return response.data.data;
    } catch (error) {
      if (error.response) {
        console.error('Erro ao buscar equipamentos:', error.response.data);
        toast.error(error.response.data.detail || 'Erro ao buscar equipamentos.');
      } else {
        console.error('Erro ao buscar equipamentos:', error.message);
        toast.error('Erro ao buscar equipamentos.');
      }
      return [];
    }
  };

  // Requisisão para listar os nomes dos equipamentos
  const listNameEquipments = async () => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }

    try {
      const response = await api.get('units/nameequipment/', {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });

      return response.data;
    } catch (error) {
      if (error.response) {
        console.error('Erro ao buscar tipos de equipamento', error);
        toast.error(error.response.data.detail);
      } else {
        console.error('Erro ao buscar tipos de equipamento', error);
        toast.error('Erro ao buscar tipos de equipamento', error);
      }
    }
  };

  // Requisisão para editar status do equipamento
  const updateEquipment = async (equipmentId, data) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      toast.error('Token de autenticação não encontrado');
      return;
    }

    console.log('Dados a serem enviados para a API:', data); // Log para debug

    try {
      const response = await api.patch(`/units/equipment/${equipmentId}/`, { status: data }, {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });

      console.log('Resposta da API:', response.data); // Log para debug
      toast.success('Equipamento atualizado com sucesso');
      return response.data;
    } catch (error) {
      console.error('Erro ao atualizar equipamento:', error.response?.data || error.message);
      toast.error('Erro ao atualizar equipamento.');
    }
  };

  // Requisisão para listar Celas
  const listCells = async (query = {}) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }

    const params = buildQueryParams(query, 'pavilion');

    try {
      const headers = {
        Authorization: `Bearer ${accessToken}`
      };

      if (!hasExplicitPagination(params)) {
        params.limit = 500;
        return await fetchAllPages('units/cell/', headers, params);
      }

      const response = await api.get('units/cell/', { params, headers });
      return toResultObject(response?.data);
    } catch (error) {
      console.error('Erro ao buscar Celas', error);
      toast.error(error.response.data.detail);
    }
  };

  // Requisisão para Cadastrar nova Celas
  const saveCell = async (cellData) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }

    try {
      const response = await api.post('units/cell/', cellData, {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });

      toast.success('Cela cadastrada com sucesso');
      return response.data;
    } catch (error) {
      if (error.response) {
        console.error('Erro ao cadastrar Cela', error);
        toast.error(error.response.data.detail);
      } else {
        console.error('Erro ao cadastrar Cela', error);
        toast.error('Erro ao cadastrar Cela', error);
      }
    }
  };

  // Editar cela
  const editCell = async (cellId, cellData) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }

    try {
      const response = await api.patch(`units/cell/${cellId}/`, cellData, {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });

      toast.success('Cela atualizada com sucesso')
      return response.data;
    } catch (error) {
      if (error.response) {
        console.error('Erro ao atualizar Cela', error);
        toast.error(error.response.data.detail);
      } else {
        console.error('Erro ao atualizar Cela', error);
        toast.error('Erro ao atualizar Cela', error);
      }
    }
  };

  const getCellById = async (cellId, options = {}) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken || !cellId) {
      return null;
    }

    const { silent = false } = options;

    try {
      const response = await api.get(`units/cell/${cellId}/`, {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });
      return response?.data || null;
    } catch (error) {
      console.error('Erro ao buscar cela por ID', error);
      if (!silent) {
        const message = error?.response?.data?.message || error?.response?.data?.detail;
        toast.error(message || 'Erro ao buscar cela.');
      }
      return null;
    }
  };

  //Listar tipo de cela
  const fetchDestination = async () => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }

    try {
      const response = await api.get('units/destination/', {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      return response.data;
    } catch (error) {
      if (error.response) {
        console.error('Erro ao buscar tipos de Celas', error);
        toast.error(error.response.data.detail);
      } else {
        console.error('Erro ao buscar tipos de Celas', error);
        toast.error('Erro ao buscar tipos de Celas', error);
      }
    }
  };

  //Listar destino da Cela
  const fetchTypeCell = async () => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }

    try {
      const response = await api.get('units/type_cell/', {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      return response.data;
    } catch (error) {
      if (error.response) {
        console.error('Erro ao buscar buscar destino de Celas', error);
        toast.error(error.response.data.detail);
      } else {
        console.error('Erro ao buscar buscar destino de Celas', error);
        toast.error('Erro ao buscar buscar destino de Celas', error);
      }
    }
  };

  // Listar tipos local de unidades
  const listLocations = async () => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }

    try {
      const response = await api.get('units/location/', {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar locais:', error);
      throw error;
    }
  };


  const listMovimentCellInternal = async (params) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }
    try {
      const response = await api.get('/internals/cellchange/', {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        params,
      });

      return response.data;
    } catch (error) {
      console.error("Erro ao listar historico:", error);
      throw error;
    }
  };

  // Listar todos os acervos de uma unidade
  const listCollection = async (unitId) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }
    try {
      const response = await api.get(`/units/collection/`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        params: { unit: unitId },
      });
      return response.data;
    } catch (error) {
      console.error("Erro ao listar acervo:", error);
      return [];
    }
  };

  // Criar um novo item no acervo
  const createCollection = async (collectionData) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }
    try {
      const response = await api.post(`/units/collection/`, collectionData, {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });
      toast.success('Acervo cadastrada com sucesso');
      return response.data;
    } catch (error) {
      console.error("Erro ao criar acervo:", error);
      toast.error('Erro ao cadastrar acervo');
      return null;
    }
  };

  // O filtro "unit" e "collection" do endpoint de livros nao funcionam (testado direto na API);
  // apenas "unit_acronym" filtra corretamente por unidade.
  const listBooks = async ({ limit = 10, offset = 0, unitAcronym, title } = {}) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }
    try {
      const params = { limit, offset };
      if (unitAcronym) params.unit_acronym = unitAcronym;
      if (title) params.title = title;
      const response = await api.get(`/units/book/`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        params,
      });
      return response.data;
    } catch (error) {
      console.error("Erro ao listar livros:", error);
      return [];
    }
  };

  // Criar livro
  const createBook = async (bookData) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }
    try {
      const response = await api.post(`/units/book/`, bookData, {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });
      toast.success('Livro cadastrado com sucesso');
      return response.data;
    } catch (error) {
      console.error("Erro ao cadastrar livro:", error);
      return null;
    }
  };

  // Editar cela
  const editBook = async (bookId, bookData) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      return;
    }

    try {
      const response = await api.patch(`units/book/${bookId}/`, bookData, {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });

      toast.success('Livro atualizada com sucesso')
      return response.data;
    } catch (error) {
      if (error.response) {
        console.error('Erro ao atualizar livro', error);
        toast.error(error.response.data.detail);
      } else {
        console.error('Erro ao atualizar livro', error);
        toast.error('Erro ao atualizar livro', error);
      }
    }
  };



  return (
    <UnitsContext.Provider value={{
      saveUnit,
      listUnits,
      getUnitById,
      updateUnit,
      saveEquipments,
      listEquipments,
      listNameEquipments,
      updateEquipment,
      listThirdPartyServices,
      listPavillions,
      savePavilion,
      updatePavilion,
      listCells,
      getCellById,
      saveCell,
      editCell,
      fetchDestination,
      fetchTypeCell,
      summaryListUnit,
      listLocations,
      listMovimentCellInternal,
      listCollection,
      createCollection,
      createBook,
      listBooks,
      editBook
    }}>
      {children}
    </UnitsContext.Provider>
  );
};
